import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Loader2, Calendar, MapPin, Users, Upload, FileText } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function EventResponsePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const interestedParam = searchParams.get('interested');
  
  const [status, setStatus] = useState('loading'); // loading, form, submitting, success, error, already_responded
  const [eventData, setEventData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [error, setError] = useState('');
  const [menuFile, setMenuFile] = useState(null);
  const [isInterested, setIsInterested] = useState(interestedParam !== 'false');

  // Load event data on mount
  useEffect(() => {
    const loadEventData = async () => {
      try {
        const eventRes = await axios.get(`${API}/api/events/respond-public/${token}`);
        setEventData(eventRes.data);
        
        // If already responded, show that status
        if (eventRes.data.already_responded) {
          setStatus('already_responded');
          setResponseData({ status: eventRes.data.request_status });
          return;
        }
        
        // If decline was clicked directly, submit immediately
        if (interestedParam === 'false') {
          await submitResponse(false, null);
        } else {
          // Show form to choose and optionally upload menu
          setStatus('form');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        setError(err.response?.data?.detail || 'Une erreur est survenue');
        setStatus('error');
      }
    };

    if (token) {
      loadEventData();
    }
  }, [token, interestedParam]);

  const submitResponse = async (interested, file) => {
    setStatus('submitting');
    try {
      const formData = new FormData();
      formData.append('interested', interested.toString());
      if (file) {
        formData.append('menu_pdf', file);
      }
      
      const response = await axios.post(
        `${API}/api/events/respond-public/${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setResponseData(response.data);
      setStatus('success');
    } catch (err) {
      console.error('Error submitting response:', err);
      setError(err.response?.data?.detail || 'Une erreur est survenue');
      setStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitResponse(isInterested, isInterested ? menuFile : null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setMenuFile(file);
    } else if (file) {
      alert('Veuillez sélectionner un fichier PDF');
      e.target.value = '';
    }
  };

  // Loading state
  if (status === 'loading' || status === 'submitting') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600">
              {status === 'loading' ? 'Chargement...' : 'Envoi de votre réponse...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-2" />
            <CardTitle className="text-red-700">Erreur</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Le lien est peut-être invalide ou expiré.<br />
              Contactez l'équipe Food Truck United si le problème persiste.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form state - show event details and ask for response + menu
  if (status === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-amber-200 shadow-lg">
          <CardHeader className="text-center bg-amber-50 rounded-t-lg">
            <CardTitle className="text-amber-700 text-2xl">
              Demande de participation
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Event details */}
            {eventData?.event && (
              <div className="bg-white rounded-lg p-4 border border-amber-200 mb-6">
                <h3 className="font-semibold text-amber-800 text-lg mb-3">
                  {eventData.event.titre}
                </h3>
                <div className="space-y-2 text-sm text-gray-600">
                  {eventData.event.date_debut && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-amber-600" />
                      <span>{new Date(eventData.event.date_debut).toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                  )}
                  {eventData.event.lieu && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-600" />
                      <span>{eventData.event.lieu}</span>
                    </div>
                  )}
                  {eventData.event.nombre_couverts && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-amber-600" />
                      <span>{eventData.event.nombre_couverts} couverts</span>
                    </div>
                  )}
                  {eventData.event.description && (
                    <p className="mt-2 text-gray-500 text-sm">{eventData.event.description}</p>
                  )}
                </div>
              </div>
            )}

            {eventData?.food_truck && (
              <p className="text-center text-sm text-gray-500 mb-4">
                Réponse pour : <strong>{eventData.food_truck.nom_food_truck}</strong>
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Choice buttons */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Votre réponse :</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setIsInterested(true)}
                    className={`flex-1 h-12 ${isInterested 
                      ? 'bg-green-500 hover:bg-green-600 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Je suis intéressé(e)
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setIsInterested(false)}
                    className={`flex-1 h-12 ${!isInterested 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Je refuse
                  </Button>
                </div>
              </div>

              {/* Menu upload - only show if interested */}
              {isInterested && (
                <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-base font-medium flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    Menu pour cet événement (PDF)
                  </Label>
                  <p className="text-sm text-gray-500">
                    Joignez votre menu au format PDF pour cet événement
                  </p>
                  <div className="flex items-center gap-3">
                    <Input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      className="flex-1"
                      data-testid="menu-pdf-input"
                    />
                  </div>
                  {menuFile && (
                    <p className="text-sm text-green-600 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {menuFile.name}
                    </p>
                  )}
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                className={`w-full h-12 text-lg ${isInterested 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-red-500 hover:bg-red-600'}`}
                data-testid="submit-response-btn"
              >
                {isInterested ? 'Confirmer mon intérêt' : 'Confirmer mon refus'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success or already_responded state
  const finalStatus = responseData?.status || eventData?.request_status;
  const isInterestedFinal = finalStatus === 'interested';
  const isDeclined = finalStatus === 'not_interested';

  const bgGradient = isDeclined ? 'from-red-50 to-white' : 'from-green-50 to-white';
  const cardBorder = isDeclined ? 'border-red-200' : 'border-green-200';
  const headerBg = isDeclined ? 'bg-red-50' : 'bg-green-50';
  const iconColor = isDeclined ? 'text-red-500' : 'text-green-500';
  const titleColor = isDeclined ? 'text-red-700' : 'text-green-700';

  return (
    <div className={`min-h-screen bg-gradient-to-b ${bgGradient} flex items-center justify-center p-4`}>
      <Card className={`w-full max-w-lg ${cardBorder} shadow-lg`}>
        <CardHeader className={`text-center ${headerBg} rounded-t-lg`}>
          {isDeclined ? (
            <XCircle className={`w-20 h-20 ${iconColor} mx-auto mb-2`} />
          ) : (
            <CheckCircle className={`w-20 h-20 ${iconColor} mx-auto mb-2`} />
          )}
          <CardTitle className={`${titleColor} text-2xl`}>
            {status === 'already_responded' ? 'Réponse déjà enregistrée' : 'Merci !'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {isInterestedFinal ? (
            <div className="text-center mb-6">
              <p className="text-lg text-gray-700 mb-2">
                Votre intérêt pour cet événement a été enregistré !
              </p>
              <p className="text-gray-500">
                Le bureau vous contactera prochainement pour finaliser votre participation.
              </p>
              {responseData?.menu_uploaded && (
                <p className="text-green-600 mt-2 flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4" />
                  Menu PDF joint avec succès
                </p>
              )}
            </div>
          ) : isDeclined ? (
            <div className="text-center mb-6">
              <p className="text-lg text-gray-700 mb-2">
                Votre refus a été enregistré.
              </p>
              <p className="text-gray-500">
                Nous espérons vous revoir lors d'un prochain événement !
              </p>
            </div>
          ) : (
            <div className="text-center mb-6">
              <p className="text-lg text-gray-700">
                Votre réponse a été enregistrée.
              </p>
            </div>
          )}

          {eventData?.event && (
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <h3 className="font-semibold text-amber-800 text-lg mb-3">
                {eventData.event.titre}
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                {eventData.event.date_debut && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-600" />
                    <span>{new Date(eventData.event.date_debut).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                )}
                {eventData.event.lieu && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-600" />
                    <span>{eventData.event.lieu}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {eventData?.food_truck && (
            <div className="mt-4 text-center text-sm text-gray-500">
              Réponse enregistrée pour : <strong>{eventData.food_truck.nom_food_truck}</strong>
            </div>
          )}

          <div className="mt-6 text-center">
            <Button 
              variant="outline" 
              onClick={() => window.close()}
              className="mr-2"
            >
              Fermer cette page
            </Button>
            <a href="/" className="inline-block">
              <Button className="bg-amber-500 hover:bg-amber-600">
                Accéder à la plateforme
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
